import styled from '@emotion/styled';
import theme from '@travelnest/merlin.theme';
import { dateEquals, DAY_BORDER_TYPES, isDateSelected, isDayBlocked } from './date-helpers';
import { ICalendarEvent } from './types';

export const SELECTED_GREEN = '#CCE4DE';
export const EVENT_GREY = '#F1F1F1';
export const DAY_IS_AVAILABLE = '#FFFFFF';

export const MOBILE_BP = '768px';

export const Container = styled.div`
	overflow: hidden;
	width: 100%;
	margin: 0;
`;

export const Grid = styled.div`
	display: grid;
	width: 100%;
`;

export const DaysOfWeekContainer = styled(Grid)`
	grid-template-columns: repeat(7, calc(100% / 7));
	grid-auto-rows: 30px;
	line-height: 30px;
	background: #fff;
`;

export const CalendarContainer = styled(Grid)`
	grid-template-columns: repeat(7, calc(100% / 7));
	grid-auto-rows: 60px;
	overflow: hidden;
	@media only screen and (min-width: ${MOBILE_BP}) {
		grid-auto-rows: 100px;
	}
`;

export const DayHeader = styled.div`

	margin: 0 2px;
	p {
		font-size: 14px;
		color: #888888;
		width: 100%;
		display: flex;
		justify-content: center;
	}

	@media only screen and (min-width: ${MOBILE_BP}) {
		margin: 0 4px;
	}
`;

export const getBackground = (eventsToday: ICalendarEvent[], currentCell: Date, selected: boolean) => {
	let background;
	// if there is no event today
	if (eventsToday.length === 0) { background = DAY_IS_AVAILABLE; }
	if (selected) { background = SELECTED_GREEN; }
	// if there is an event today
	if (!selected && eventsToday.length > 0) {
		// if there is only one event today
		if (eventsToday.length === 1) {
			// if the event starts today
			if (dateEquals(eventsToday[0]!.start, currentCell)) {
				background = EVENT_GREY;
			} else if (dateEquals(eventsToday[0]!.end, currentCell)) {
				// it ends today
				background = DAY_IS_AVAILABLE;
			} else {
				// we are in the middle of an event
				background = EVENT_GREY;
			}
		} else {
			// the maximum length today can be is 2 (one event ends and the next one starts)
			background = EVENT_GREY;
		}
	}
	return `background-color: ${background};`;
};

const getBlockedBackground = (blockedAndSelected: boolean) => `
	background-color: ${blockedAndSelected ? SELECTED_GREEN : EVENT_GREY};
	color: ${theme.palette.greyscale.dark};
	svg {
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		position: absolute;
		stroke: ${theme.palette.greyscale.medium};
	}
`;

/**
 * This function returns css to be applied to an individual cell based on
 * the context of an event and whether or not the cell is selected.
 * background-image properties can stack so we apply the logic separately
 * and then stack them on top of each other to get the desired effect.
 */
export const getCellStyling = (eventsToday: ICalendarEvent[], selectedDates: Date[], currentCell: Date, overspill: boolean) => {
	// if we are overspill return nothing
	if (overspill) return '';
	const selected = isDateSelected(selectedDates, currentCell);
	const backgroundCss = isDayBlocked(eventsToday, currentCell) ? getBlockedBackground(selected) : getBackground(eventsToday, currentCell, selected);
	return backgroundCss;
};

const border = '1px solid #EAEAEA';
const hiddenBorder = '1px solid rgba(0, 0, 0, 0)';

export const Day = styled.div<{ overspill: boolean, selected: boolean, additionalBorder: DAY_BORDER_TYPES }>`
	position: relative;
	background-color: #ffffff;
	width: 100%;
	height: 100%;
	padding: 5px;
	letter-spacing: 1px;
	font-size: 14px;
	box-sizing: border-box;
	color: #757575;
	outline: none;
	// webkit styles here are for weird iOS styling that get applied on tap
	-webkit-tap-highlight-color: transparent;
	-webkit-touch-callout: none;

	${({ overspill }) => !overspill && `
		@media screen and (min-width: ${MOBILE_BP}) {
			transition: border-color 0.2s;
			&:focus,&:active,&:hover {
				border: 1px solid #757575;
				cursor: pointer;
			}
		}
		border-right: ${border};
		border-bottom: ${border};
	` || `border: ${hiddenBorder};`}

	${({ additionalBorder }) => {
		switch (additionalBorder) {
			case DAY_BORDER_TYPES.NONE:
			default:
				return `border-left: ${hiddenBorder}; border-top: ${hiddenBorder};`;
			case DAY_BORDER_TYPES.BOTH:
				return `border-left: ${border}; border-top: ${border};`;
			case DAY_BORDER_TYPES.LEFT:
				return `border-left: ${border}; border-top: ${hiddenBorder};`;
			case DAY_BORDER_TYPES.TOP:
				return `border-left: ${hiddenBorder}; border-top: ${border};`;
			// Only the initial overspill (hidden) dates will get this type
			case DAY_BORDER_TYPES.BOTTOM:
				return `border-left: ${hiddenBorder}; border-bottom: ${border};`;
			// Only the last initial overspill day (next to 1st of month) will get this type
			case DAY_BORDER_TYPES.BOTTOM_RIGHT:
				return `border-right: ${border}; border-bottom: ${border};`;
		}
	}}

	${({ selected, overspill }) => selected && !overspill && `background-color: ${SELECTED_GREEN};`};

	&:nth-of-type(7n + 1) {
		// gets every 7th day plus 1 which is the left hand side of the calendar
		grid-column: 1/1;
	}

	&:nth-of-type(7n + 2) {
		grid-column: 2/2;
	}

	&:nth-of-type(7n + 3) {
		grid-column: 3/3;
	}

	&:nth-of-type(7n + 4) {
		grid-column: 4/4;
	}

	&:nth-of-type(7n + 5) {
		grid-column: 5/5;
	}

	&:nth-of-type(7n + 6) {
		grid-column: 6/6;
	}

	&:nth-of-type(7n + 7) {
		grid-column: 7/7;
	}

	&:nth-of-type(n + 1):nth-of-type(-n + 7) {
		// assures the first 7 children are row 1
		grid-row: 1;
	}

	&:nth-of-type(n + 8):nth-of-type(-n + 14) {
		grid-row: 2;
	}

	&:nth-of-type(n + 15):nth-of-type(-n + 21) {
		grid-row: 3;
	}

	&:nth-of-type(n + 22):nth-of-type(-n + 28) {
		grid-row: 4;
	}

	&:nth-of-type(n + 29):nth-of-type(-n + 35) {
		grid-row: 5;
	}

	&:nth-of-type(n + 36):nth-of-type(-n + 42) {
		grid-row: 6;
	}
`;

export const Prices = styled.div`
	position: absolute;
	right: 0;
	bottom: 0;
	padding: 5px;
	font-weight: bold;
	font-size: 12px;
	font-family: sans-serif;
	color: ${theme.palette.greyscale.darkest};
	text-align: right;
	@media screen and (min-width: ${MOBILE_BP}) {
		font-size: 14px;
	}
`;

export const OverridePrice = styled.div`
	color: #1D6CF8;
`;

export const Price = styled.div<{ overrideExists: boolean }>`
	${({ overrideExists }) => overrideExists && `
		text-decoration-line: line-through;
		color: rgba(68, 68, 68, 0.2);
	`}
`;
